<template>
  <div ref="headerRef" class="container-selfservice">
    <DashboardSidebar @update:menuCurrent="movePage" />
    <div :class="['container-selfservice-content']" data-scroll-container>
      <!--HEADER-->
      <ChatHeader
        v-if="isVisibleChatHeader"
        is-menu-open
        domain="SELF_SERVICE"
        :is-using-credit="isUsingCredit"
        :is-using-back-btn="isMobileSize"
        :type-call
        @back="goBack"
      />

      <DashboardHeader v-else domain="self-service" :is-using-credit="isUsingCredit" class="top" />

      <SelfServiceCreditAlert v-if="isVisibleCreditAlert" />

      <router-view v-slot="{ Component }">
        <component :is="Component" />
      </router-view>
    </div>

    <DashboardBottomNav v-if="!isMobile && isMobileSize && !isChatConnected" domain="self-service" class="bottom" />

    <!--MODAL-->
    <teleport to="body">
      <ModalSelectLanguage
        v-if="isVisibleModalLanguage"
        :current-language="getLanguageByCode(userStore.pageLanguage)"
        :subtitle-key="t('label_choose_display_language')"
        @close="toggleModalLanguage()"
      />
      <PaymentSettingsModalAutoRefill v-if="isVisibleModalRefill" />
      <SelfServiceModalSurvey v-if="isVisibleModalSurvey" />
      <SelfServiceModalPolicy v-if="isVisibleModalPolicy" />
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { fetchAllRegisteredDevices, fetchInfoPartner } from '@/api/apiService';
import ChatHeader from '@/components/domains/chat/common/ChatHeader.vue';
import ModalSelectLanguage from '@/components/domains/chat/common/ModalSelectLanguage.vue';
import SelfServiceModalPolicy from '@/components/domains/self-service/common/SelfServiceModalPolicy.vue';
import SelfServiceModalSurvey from '@/components/domains/self-service/common/SelfServiceModalSurvey.vue';
import PaymentSettingsModalAutoRefill from '@/components/domains/self-service/payment-settings/PaymentSettingsModalAutoRefill.vue';
import SelfServiceCreditAlert from '@/components/domains/self-service/SelfServiceCreditAlert.vue';
import DashboardBottomNav from '@/components/shared/composite/DashboardBottomNav.vue';
import DashboardHeader from '@/components/shared/composite/DashboardHeader.vue';
import DashboardSidebar from '@/components/shared/composite/DashboardSidebar.vue';
import { useHangUpCommon } from '@/composables/domains/chat/useHangupCommon';
import { useModalPolicy } from '@/composables/domains/self-service/useModalPolicy';
import { useModalRefill } from '@/composables/domains/self-service/useModalRefill';
import { useModalSurvey } from '@/composables/domains/self-service/useModalSurvey';
import { useNotification } from '@/composables/shared/useNotification';
import useSize from '@/composables/shared/useSize';
import { scrollPositions } from '@/router';
import { getLanguageByCode } from '@/static/languages';
import { useAdminStore } from '@/stores/admin';
import { useAlertStore } from '@/stores/alert';
import { useAudioStore } from '@/stores/audio';
import { useCountryStore } from '@/stores/country';
import { useSocketStore } from '@/stores/socket';
import { useSubscriptionStore } from '@/stores/subscription';
import { useUserStore } from '@/stores/user';
import { TypeCall } from '@/types/common';
import { CHAT_TYPES } from '@/utils/constants/chat';
import { isInCapacitorApp } from '@/utils/device';
import {
  ij_getListVoiceRegistered,
  ij_hasVoiceRegistered,
  ij_isVisibleButtonMyLanguage,
  ij_isVisibleSecretController,
  ij_isVisibleSecretLangaugeChange,
  ij_toggleIsVisibleSecretController,
  ij_toggleIsVisibleSecretLangaugeChange,
  ij_toggleModalLanguage,
  ij_domain,
} from '@/utils/injectionKeys';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const adminStore = useAdminStore();
const audioStore = useAudioStore();
const alertStore = useAlertStore();
const countryStore = useCountryStore();
const socketStore = useSocketStore();
const subscriptionStore = useSubscriptionStore();
const userStore = useUserStore();

const { isMobileSize } = useSize('headerRef');
const { alertEndCall } = useNotification();
const { isVisibleModalRefill } = useModalRefill();
const { isVisibleModalSurvey } = useModalSurvey();
const { isVisibleModalPolicy } = useModalPolicy();
const { handleHangUp } = useHangUpCommon({ setBottomBarStatus: () => {} });

const isVisibleModalLanguage = ref(false); // 언어선택 모달 보임 여부
const hasVoiceRegistered = ref(false); // 등록한 음성이 있는지
const isVisibleSecretController = ref<boolean>(false); // "멀티 채널 지원 보기" 노출 여부
const isVisibleButtonMyLanguage = ref<boolean>(true); // "나의 언어만 보기" 노출 여부
const isVisibleSecretLangaugeChange = ref<boolean>(false); // "테스터 전용 컨트롤러" 노출 여부
const isVisibleCreditAlert = ref<boolean>(false);

const isMobile = isInCapacitorApp();
let intervalAuthCheck = 0;

const isCreditWarning = computed<boolean>(() => {
  return (
    adminStore.isHost &&
    (subscriptionStore.usagePercentage > 90 ||
      subscriptionStore.currentCredits === 0 ||
      !!subscriptionStore.lastPaymentErrorCode)
  );
});

const isUsingCredit = computed<boolean>(() => {
  return adminStore.isHost;
});

const isVisibleChatHeader = computed(() => {
  return (typeCall.value && isMobile) || isChatConnected.value;
});

const typeCall = computed<TypeCall | undefined>(() => {
  const { chatType } = route.meta;
  return Object.values(CHAT_TYPES).includes(chatType as TypeCall) ? chatType : undefined;
});

const isChatConnected = computed<boolean>(() => {
  return !!typeCall.value && socketStore.socketState !== 'CLOSED';
});

const isExitConfirmed = ref(false);
let unregisterNavigationGuard: (() => void) | null = null;

const exitChat = () => {
  if (!['SINGLE', 'AUDIENCE'].includes(typeCall.value ?? '')) {
    socketStore.close();
  }
  // if (isInCapacitorApp()) {
  //   userStore.setPageLanguage(getBrowserLanguage());
  // }
};

const goBack = () => {
  alertEndCall(() => {
    isExitConfirmed.value = true;
    exitChat();
    router.back();
  });
};

const handleGuardExit = (to, from, next) => {
  // 채팅방에서 나가는 경우에만 인터셉트 (진입 시에는 통과)
  if (!from.meta?.chatType) {
    next();
    return;
  }
  // to도 chatType을 가지면 채팅방 내부 이동(쿼리 업데이트 등)이므로 통과
  if (to.meta?.chatType) {
    next();
    return;
  }
  if (isExitConfirmed.value) {
    isExitConfirmed.value = false;
    next();
    return;
  }
  if (userStore.isPendingLogout) {
    next();
    return;
  }
  if (alertStore.visible) {
    next(false);
    return;
  }

  alertEndCall(() => {
    isExitConfirmed.value = true;
    exitChat();
    router.replace(null);
  });
  next(false);
};

// 언어선택 모달 여닫기
const toggleModalLanguage = () => {
  isVisibleModalLanguage.value = !isVisibleModalLanguage.value;
};

const toggleIsVisibleSecretController = () => {
  isVisibleSecretController.value = !isVisibleSecretController.value;
};

const toggleIsVisibleSecretLanguageChange = () => {
  isVisibleSecretLangaugeChange.value = !isVisibleSecretLangaugeChange.value;
};

// 등록된 음성 정보 조회
const getListVoiceRegistered = async () => {
  const { data, success } = await fetchAllRegisteredDevices();
  if (success) hasVoiceRegistered.value = data.length > 0;
};

const movePage = (name: string) => {
  router.push({ name: `ss-${name}` });
};

// 30초 마다 계정정보 조회
const startIntervalAuthCheck = () => {
  if (intervalAuthCheck) clearInterval(intervalAuthCheck);
  intervalAuthCheck = setInterval(async () => {
    await adminStore.fetchPartnerHome();
    await fetchInfoPartner(adminStore.getPartnerInfo.partnerCode);

    // 내 계정이나 PartnerCode가 존재하지 않을 경우
    if (!adminStore.getPartnerEmployeeInfo.userId || !adminStore.getPartnerInfo.partnerName) {
      handleHangUp();
    }
  }, 30000);
};

onMounted(async () => {
  await subscriptionStore.initializeSubscriptionInfo();
  await countryStore.requestCountries();
  await adminStore.fetchPartnerHome();
  if (!adminStore.isHost) {
    startIntervalAuthCheck();
  }

  const el = document.querySelector('[data-scroll-container]');
  el?.addEventListener('scroll', () => {
    scrollPositions.set(router.currentRoute.value.fullPath, el.scrollTop);
  });
});

watch(
  () => subscriptionStore.isSubscriptionLoading,
  (isLoading) => {
    if (!isLoading) {
      isVisibleCreditAlert.value = isCreditWarning.value;
    }
  }
);

onUnmounted(() => {
  clearInterval(intervalAuthCheck);
  unregisterNavigationGuard?.();
});

// 채팅 연결 시 뒤로가기 인터셉트 (물리 버튼 포함)
watch(
  isChatConnected,
  (connected) => {
    if (connected && !isMobile) {
      unregisterNavigationGuard = router.beforeEach(handleGuardExit);
    } else {
      unregisterNavigationGuard?.();
      unregisterNavigationGuard = null;
    }
  },
  { immediate: true }
);

// "나의 언어만 보기" 버튼 노출 여부를 결정합니다.
watch(
  () => audioStore.isSingleLanguage,
  (newVal) => {
    isVisibleButtonMyLanguage.value = !newVal;
  },
  { immediate: true }
);

watch(
  () => userStore.isPendingLogout,
  (isPending) => {
    if (isPending) {
      isExitConfirmed.value = true;
    }
  }
);

getListVoiceRegistered();

provide(ij_hasVoiceRegistered, hasVoiceRegistered);
provide(ij_toggleModalLanguage, toggleModalLanguage);
provide(ij_getListVoiceRegistered, getListVoiceRegistered);
provide(ij_isVisibleButtonMyLanguage, isVisibleButtonMyLanguage);
provide(ij_isVisibleSecretController, isVisibleSecretController);
provide(ij_toggleIsVisibleSecretController, toggleIsVisibleSecretController);
provide(ij_isVisibleSecretLangaugeChange, isVisibleSecretLangaugeChange);
provide(ij_toggleIsVisibleSecretLangaugeChange, toggleIsVisibleSecretLanguageChange);
provide(ij_domain, 'SELF_SERVICE');
</script>

<style scoped lang="scss">
.container-selfservice {
  display: flex;
  height: 100dvh;
  min-height: 100dvh;
  max-height: 100dvh;
  background: var(--background-theme);
  overflow-y: hidden;

  @include respond-to(mobile-landscape) {
    flex-direction: column;
  }
}

.container-selfservice-content {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow-y: auto;

  &.fixed-content {
    height: 100dvh;
    min-height: 100dvh;
    max-height: 100dvh;
    overflow-y: hidden;
  }
  @include respond-to(mobile-landscape) {
    flex: 1;
    &::-webkit-scrollbar {
      display: none;
    }
  }
}

.bottom {
  display: none;

  @include respond-to(mobile-landscape) {
    display: flex;
  }
}
</style>
